/**
 * Investigation Notebook — named cases with pinned outputs, notes, and export.
 * Left-side drawer UI. Pure localStorage via notebook-store.js.
 */

import {
  getCaseIndex, getActiveCaseId, setActiveCaseId,
  createCase, renameCase, deleteCase,
  getEntries, addEntry, deleteEntry, updateNote,
  exportCase, importCase,
} from './utils/notebook-store.js';
import { copyToClipboard } from './utils/ui.js';
import { openInTool } from './panel.js';
import './styles/tools/_notebook.scss';
import { escapeHtml } from './utils/html.js';
import { timeAgo } from './utils/date-format.js';

let _drawer;
let _previousFocus = null;
let _closeSessionsDrawer = null;

function truncate(str, len) {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '\u2026' : str;
}

// ─── Render ──────────────────────────────────────────────────────────────────

function renderDrawer() {
  const content = _drawer.querySelector('.ntb-content');
  const cases = getCaseIndex();
  const activeId = getActiveCaseId();
  const activeCase = cases.find((c) => c.id === activeId);
  const entries = activeCase ? getEntries(activeId) : [];

  // Active case section
  let activeCaseHtml;
  if (activeCase) {
    activeCaseHtml = `
      <div class="ntb-active-case">
        <div class="ntb-active-header">
          <input class="ntb-case-name" type="text" value="${escapeHtml(activeCase.name)}"
            data-action="rename" data-cid="${activeCase.id}" spellcheck="false" />
          <span class="ntb-entry-count">${activeCase.entryCount} item${activeCase.entryCount !== 1 ? 's' : ''}</span>
        </div>
      </div>`;
  } else {
    activeCaseHtml = `
      <div class="ntb-empty-state">
        <p class="ntb-empty-text">No active case</p>
        <button class="ntb-create-btn" data-action="create">New Case</button>
      </div>`;
  }

  // Entries
  let entriesHtml = '';
  if (activeCase && entries.length) {
    entriesHtml = entries.map((e) => {
      if (e.type === 'pin') {
        return `
          <div class="ntb-entry ntb-entry--pin" data-eid="${e.id}">
            <div class="ntb-entry-header">
              <span class="ntb-entry-tool">${escapeHtml(e.toolLabel || e.toolId)}</span>
              <span class="ntb-entry-time">${timeAgo(e.created)}</span>
            </div>
            ${e.inputSummary ? `<div class="ntb-entry-input">${escapeHtml(truncate(e.inputSummary, 60))}</div>` : ''}
            <div class="ntb-entry-content">${escapeHtml(truncate(e.content, 200))}</div>
            <div class="ntb-entry-actions">
              <button class="ntb-btn" data-action="copy-entry" data-eid="${e.id}">Copy</button>
              <button class="ntb-btn" data-action="open-in" data-tool="${escapeHtml(e.toolId)}" data-eid="${e.id}">Open In</button>
              <button class="ntb-btn ntb-btn--danger" data-action="del-entry" data-eid="${e.id}">Remove</button>
            </div>
          </div>`;
      }
      if (e.type === 'note') {
        return `
          <div class="ntb-entry ntb-entry--note" data-eid="${e.id}">
            <div class="ntb-entry-header">
              <span class="ntb-entry-tool">Note</span>
              <span class="ntb-entry-time">${timeAgo(e.created)}</span>
            </div>
            <textarea class="ntb-note-text" data-action="edit-note" data-eid="${e.id}"
              rows="2" spellcheck="false">${escapeHtml(e.text || '')}</textarea>
            <div class="ntb-entry-actions">
              <button class="ntb-btn ntb-btn--danger" data-action="del-entry" data-eid="${e.id}">Remove</button>
            </div>
          </div>`;
      }
      return '';
    }).join('');
  } else if (activeCase) {
    entriesHtml = '<p class="ntb-empty-text">Pin results or add notes to build your case</p>';
  }

  // Add note row
  const noteRow = activeCase
    ? `<div class="ntb-add-row">
        <input type="text" class="ntb-add-input" id="ntb-note-input" placeholder="Add a note..." autocomplete="off" />
        <button class="ntb-add-btn" data-action="add-note">Add</button>
      </div>`
    : '';

  // Case list
  const caseListHtml = cases.length
    ? cases.map((c) => `
        <div class="ntb-case-item${c.id === activeId ? ' ntb-case-item--active' : ''}">
          <button class="ntb-case-select" data-action="select-case" data-cid="${c.id}">
            <span class="ntb-case-item-name">${escapeHtml(c.name)}</span>
            <span class="ntb-case-item-meta">${c.entryCount} item${c.entryCount !== 1 ? 's' : ''} \u00b7 ${timeAgo(c.updated)}</span>
          </button>
          <button class="ntb-case-del" data-action="del-case" data-cid="${c.id}" aria-label="Delete case">\u2715</button>
        </div>`).join('')
    : '<p class="ntb-empty-text">No cases yet</p>';

  content.innerHTML = `
    <div class="ntb-section">
      ${activeCaseHtml}
    </div>
    ${activeCase ? `
    <div class="ntb-section">
      <h3 class="ntb-section-title">Entries</h3>
      ${noteRow}
      <div class="ntb-entries">${entriesHtml}</div>
    </div>` : ''}
    <div class="ntb-section">
      <div class="ntb-cases-header">
        <h3 class="ntb-section-title">Cases</h3>
        <button class="ntb-btn" data-action="create">+ New</button>
      </div>
      <div class="ntb-case-list">${caseListHtml}</div>
    </div>
    <div class="ntb-section">
      <h3 class="ntb-section-title">Data</h3>
      <div class="ntb-data-row">
        ${activeCase ? `<button class="ntb-btn" data-action="export" data-cid="${activeId}">Export Case</button>` : ''}
        <button class="ntb-btn" data-action="import">Import</button>
      </div>
    </div>`;
}

// ─── Focus trap ──────────────────────────────────────────────────────────────

function trapFocus(e) {
  if (e.key !== 'Tab') return;
  const panel = _drawer.querySelector('.ntb-panel');
  const focusable = [...panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

// ─── Open / Close ────────────────────────────────────────────────────────────

function isOpen() {
  return _drawer.classList.contains('is-open');
}

function open() {
  if (_closeSessionsDrawer) _closeSessionsDrawer();
  _previousFocus = document.activeElement;
  renderDrawer();
  _drawer.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  _drawer.querySelector('.ntb-close')?.focus();
  _drawer.addEventListener('keydown', trapFocus);
}

function close() {
  _drawer.classList.remove('is-open');
  document.body.style.overflow = '';
  _drawer.removeEventListener('keydown', trapFocus);
  _previousFocus?.focus();
}

export function closeNotebook() {
  if (isOpen()) close();
}

export function refreshNotebook() {
  if (isOpen()) renderDrawer();
}

// ─── File import helper ──────────────────────────────────────────────────────

function triggerImport() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = importCase(reader.result);
      if (result) {
        setActiveCaseId(result.id);
        renderDrawer();
      }
    };
    reader.readAsText(file);
  });
  input.click();
}

function triggerExport(caseId) {
  const json = exportCase(caseId);
  if (!json) return;
  const index = getCaseIndex();
  const c = index.find((x) => x.id === caseId);
  const name = c ? c.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : 'case';
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `unveil-${name}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Init ────────────────────────────────────────────────────────────────────

export function initNotebook({ closeSessionsFn }) {
  _closeSessionsDrawer = closeSessionsFn;

  _drawer = document.createElement('div');
  _drawer.className = 'ntb-drawer';
  _drawer.setAttribute('role', 'dialog');
  _drawer.setAttribute('aria-modal', 'true');
  _drawer.setAttribute('aria-label', 'Investigation Notebook');
  _drawer.innerHTML = `
    <div class="ntb-backdrop"></div>
    <div class="ntb-panel">
      <div class="ntb-header">
        <h2 class="ntb-title">Notebook</h2>
        <button class="ntb-close" aria-label="Close">\u2715</button>
      </div>
      <div class="ntb-content"></div>
    </div>`;
  document.body.appendChild(_drawer);

  // Open trigger
  document.getElementById('notebook-open-btn')?.addEventListener('click', () => {
    if (isOpen()) close();
    else open();
  });

  // Close triggers
  _drawer.querySelector('.ntb-backdrop').addEventListener('click', close);
  _drawer.querySelector('.ntb-close').addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close();
  });

  // Note debounce timer
  let _noteTimer = null;

  // Delegated actions
  _drawer.addEventListener('click', (e) => {
    const el = e.target.closest('[data-action]');
    if (!el) return;
    const action = el.dataset.action;
    const activeId = getActiveCaseId();

    if (action === 'create') {
      const c = createCase('New Case');
      if (c) renderDrawer();
    }

    if (action === 'select-case') {
      setActiveCaseId(el.dataset.cid);
      renderDrawer();
    }

    if (action === 'del-case') {
      e.stopPropagation();
      deleteCase(el.dataset.cid);
      renderDrawer();
    }

    if (action === 'del-entry' && activeId) {
      deleteEntry(activeId, el.dataset.eid);
      renderDrawer();
    }

    if (action === 'add-note' && activeId) {
      const input = document.getElementById('ntb-note-input');
      const text = input?.value?.trim();
      if (text) {
        addEntry(activeId, { type: 'note', text });
        input.value = '';
        renderDrawer();
      }
    }

    if (action === 'copy-entry') {
      const entries = getEntries(activeId);
      const entry = entries.find((x) => x.id === el.dataset.eid);
      if (entry?.content) copyToClipboard(entry.content, el);
    }

    if (action === 'open-in') {
      const entries = getEntries(activeId);
      const entry = entries.find((x) => x.id === el.dataset.eid);
      if (entry?.content) {
        close();
        openInTool(el.dataset.tool, { text: entry.content });
      }
    }

    if (action === 'export') {
      triggerExport(el.dataset.cid);
    }

    if (action === 'import') {
      triggerImport();
    }
  });

  // Inline rename on blur/enter
  _drawer.addEventListener('input', (e) => {
    if (e.target.dataset.action === 'rename') {
      const cid = e.target.dataset.cid;
      const val = e.target.value.trim();
      if (val) renameCase(cid, val);
    }

    if (e.target.dataset.action === 'edit-note') {
      const eid = e.target.dataset.eid;
      const text = e.target.value;
      clearTimeout(_noteTimer);
      _noteTimer = setTimeout(() => {
        updateNote(getActiveCaseId(), eid, text);
      }, 400);
    }
  });

  // Enter in note input adds note
  _drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.id === 'ntb-note-input') {
      e.preventDefault();
      _drawer.querySelector('[data-action="add-note"]')?.click();
    }
  });
}
